import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { api } from '../../api/client';

export default function AdminSettings() {
    const [profile, setProfile] = useState({
        full_name: '',
        email: '',
        password: '',
        confirm_password: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, []);

    // Auto-dismiss success messages after 3 seconds
    useEffect(() => {
        if (message.type === 'success') {
            const timer = setTimeout(() => {
                setMessage({ type: '', text: '' });
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [message]);

    const fetchProfile = async () => {
        try {
            const res = await api.get('/api/admin/profile');
            setProfile({
                full_name: res.data.full_name || '',
                email: res.data.email || '',
                password: '',
                confirm_password: ''
            });
        } catch (err) {
            console.error('Failed to load profile:', err);
            setMessage({ type: 'error', text: 'Failed to load profile' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
        // Clear message when user starts typing
        if (message.text) {
            setMessage({ type: '', text: '' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate passwords match if changing password
        if (profile.password && profile.password !== profile.confirm_password) {
            setMessage({ type: 'error', text: 'Passwords do not match' });
            return;
        }

        // Validate password length
        if (profile.password && profile.password.length < 8) {
            setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
            return;
        }

        setSaving(true);
        try {
            const updateData = {
                full_name: profile.full_name,
                email: profile.email
            };

            // Only include password if it's being changed
            if (profile.password) {
                updateData.password = profile.password;
            }

            await api.put('/api/admin/profile', updateData);
            setMessage({ type: 'success', text: 'Profile updated successfully!' });

            // Clear password fields after successful update
            setProfile({ ...profile, password: '', confirm_password: '' });
        } catch (err) {
            console.error('Failed to update profile:', err);
            setMessage({ type: 'error', text: 'Failed to update profile' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <h1 className="text-3xl font-bold">Admin Settings</h1>

            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">Update Profile</h2>

                {message.text && (
                    <div className={`mb-4 p-3 rounded ${message.type === 'success'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                        }`}>
                        {message.text}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block mb-1 text-sm font-medium">Full Name</label>
                        <input
                            type="text"
                            name="full_name"
                            value={profile.full_name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            maxLength={30}
                            required
                        />
                        <p className="text-xs text-gray-500 mt-1">{profile.full_name.length}/30 characters</p>
                    </div>

                    <div>
                        <label className="block mb-1 text-sm font-medium">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={profile.email}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            required
                        />
                    </div>

                    <hr className="my-6" />

                    <div>
                        <h3 className="text-lg font-medium mb-3">Change Password</h3>
                        <p className="text-sm text-gray-600 mb-4">Leave blank to keep current password</p>
                    </div>

                    <div>
                        <label className="block mb-1 text-sm font-medium">New Password</label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={profile.password}
                                onChange={handleChange}
                                maxLength={15}
                                className="w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Enter new password (min. 8 characters)"
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                tabIndex={-1}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? (
                                    <Eye className="w-5 h-5" />
                                ) : (
                                    <EyeOff className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block mb-1 text-sm font-medium">Confirm New Password</label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirm_password"
                                value={profile.confirm_password}
                                onChange={handleChange}
                                maxLength={15}
                                className="w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Confirm new password"
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                                tabIndex={-1}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                {showConfirmPassword ? (
                                    <Eye className="w-5 h-5" />
                                ) : (
                                    <EyeOff className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    {profile.password && profile.confirm_password &&
                        profile.password !== profile.confirm_password && (
                            <p className="text-sm text-red-600">
                                ⚠ Passwords do not match
                            </p>
                        )}

                    {profile.password && profile.password.length > 0 && profile.password.length < 8 && (
                        <p className="text-sm text-red-600">
                            ⚠ Password must be at least 8 characters long
                        </p>
                    )}

                    <div className="flex gap-4 pt-4">
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                            {saving ? 'Saving...' : 'Update Profile'}
                        </button>
                    </div>
                </form>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold mb-4">Additional Settings</h2>
                <p className="text-gray-600">More admin settings will be available here in future updates:</p>
                <ul className="list-disc list-inside mt-3 text-gray-600 space-y-1">
                    <li>System configuration</li>
                    <li>Email notifications</li>
                    <li>Security settings</li>
                    <li>Backup and restore</li>
                    <li>API rate limits</li>
                </ul>
            </div>
        </div>
    );
}